/* eslint-disable max-lines */
import {
  PresenceLiveness,
  PresenceRule,
  PresenceRuleType,
} from '@hakimo-ui/hakimo/types';

/** The server's name for the implicit whole-camera zone */
export const WHOLE_CAMERA_ZONE = '__frame__';
export const WHOLE_CAMERA_LABEL = 'Whole camera';

/** The worker evaluates over a 60-minute window; the API refuses more. */
export const MAX_VISITOR_WINDOW_MINUTES = 60;

export interface RuleTypeOption {
  id: PresenceRuleType;
  label: string;
  hint: string;
}

export const RULE_TYPE_OPTIONS: RuleTypeOption[] = [
  {
    id: 'dwell',
    label: 'Someone stays',
    hint: 'A person is in the zone for longer than allowed.',
  },
  {
    id: 'absence',
    label: 'Nobody present',
    hint: 'The zone is empty for longer than allowed.',
  },
  {
    id: 'max_occupancy',
    label: 'Too many people',
    hint: 'The zone holds at least this many people.',
  },
  {
    id: 'min_occupancy',
    label: 'Too few people',
    hint: 'The zone holds fewer than this many people for too long.',
  },
  {
    id: 'max_visitors',
    label: 'Too many visitors',
    hint: 'More different people pass through than allowed in a window.',
  },
];

const DEFAULT_THRESHOLD_MINUTES: Record<PresenceRuleType, number> = {
  dwell: 5,
  absence: 10,
  max_occupancy: 0,
  min_occupancy: 10,
  max_visitors: 0,
};

const DEFAULT_COUNT: Record<PresenceRuleType, number> = {
  dwell: 1,
  absence: 1,
  max_occupancy: 10,
  min_occupancy: 2,
  max_visitors: 30,
};

export function usesCount(type: PresenceRuleType): boolean {
  return (
    type === 'max_occupancy' ||
    type === 'min_occupancy' ||
    type === 'max_visitors'
  );
}

export function usesMinutes(type: PresenceRuleType): boolean {
  return type !== 'max_visitors';
}

export function usesWindow(type: PresenceRuleType): boolean {
  return type === 'max_visitors';
}

/** Rules that read silence as evidence, and so depend on clip cadence. */
export function isLivenessGated(type: PresenceRuleType): boolean {
  return type === 'absence' || type === 'min_occupancy';
}

export function defaultRule(
  type: PresenceRuleType,
  zone: { id: string | null; name: string },
  cameraId: string
): PresenceRule {
  return {
    type,
    zoneId: zone.id,
    zoneName: zone.name,
    cameraId,
    enabled: true,
    count: DEFAULT_COUNT[type],
    thresholdMinutes: DEFAULT_THRESHOLD_MINUTES[type],
    windowMinutes: 60,
    minPresenceRatio: 0.1,
    livenessGapMinutes: 5,
  };
}

/** Switch an existing rule's type, keeping what still applies. */
export function withType(
  rule: PresenceRule,
  type: PresenceRuleType
): PresenceRule {
  if (rule.type === type) return rule;
  return {
    ...rule,
    type,
    count: usesCount(type) ? rule.count || DEFAULT_COUNT[type] : 1,
    thresholdMinutes: DEFAULT_THRESHOLD_MINUTES[type],
  };
}

export function zoneLabel(zoneName: string): string {
  return zoneName === WHOLE_CAMERA_ZONE ? WHOLE_CAMERA_LABEL : zoneName;
}

export function isWholeCamera(zoneName: string): boolean {
  return zoneName === WHOLE_CAMERA_ZONE;
}

/**
 * Does this rule address that zone?
 *
 * The id is the answer whenever the rule carries one -- a rename cannot
 * detach it, and an id this camera does not have is not a match. Only a
 * legacy rule, written before ids, is matched by the name it carries.
 */
export function ruleTargetsZone(
  rule: PresenceRule,
  zone: { id: string; name: string }
): boolean {
  return rule.zoneId ? rule.zoneId === zone.id : rule.zoneName === zone.name;
}

function minutes(n: number): string {
  return `${n} minute${n === 1 ? '' : 's'}`;
}

function people(n: number): string {
  return `${n} ${n === 1 ? 'person' : 'people'}`;
}

/** The rule as the sentence the form builds. */
export function describeRule(rule: PresenceRule): string {
  if (isWholeCamera(rule.zoneName)) return describeWholeCamera(rule);
  const zone = zoneLabel(rule.zoneName);
  switch (rule.type) {
    case 'dwell':
      return `A person stays in ${zone} for ${minutes(rule.thresholdMinutes)}`;
    case 'absence':
      return `${zone} is empty for ${minutes(rule.thresholdMinutes)}`;
    case 'max_occupancy':
      return rule.thresholdMinutes > 0
        ? `${zone} has ${rule.count} or more people for ${minutes(
            rule.thresholdMinutes
          )}`
        : `${zone} has ${rule.count} or more people`;
    case 'min_occupancy':
      return `${zone} has fewer than ${people(rule.count)} for ${minutes(
        rule.thresholdMinutes
      )}`;
    case 'max_visitors':
      return `More than ${people(rule.count)} pass through ${zone} in ${minutes(
        rule.windowMinutes
      )}`;
  }
}

/** The whole-camera zone is not a place, so the sentence says "in view". */
function describeWholeCamera(rule: PresenceRule): string {
  switch (rule.type) {
    case 'dwell':
      return `A person stays in view for ${minutes(rule.thresholdMinutes)}`;
    case 'absence':
      return `Nobody in view for ${minutes(rule.thresholdMinutes)}`;
    case 'max_occupancy':
      return rule.thresholdMinutes > 0
        ? `${rule.count} or more people in view for ${minutes(
            rule.thresholdMinutes
          )}`
        : `${rule.count} or more people in view`;
    case 'min_occupancy':
      return `Fewer than ${people(rule.count)} in view for ${minutes(
        rule.thresholdMinutes
      )}`;
    case 'max_visitors':
      return `More than ${people(rule.count)} pass through view in ${minutes(
        rule.windowMinutes
      )}`;
  }
}

/** Why the rule cannot be saved, or null. Mirrors the API's checks. */
export function validateRule(rule: PresenceRule): string | null {
  if (!rule.zoneId && !rule.zoneName) return 'Choose a zone.';
  if (usesCount(rule.type) && (!Number.isInteger(rule.count) || rule.count < 1))
    return 'Count must be a whole number of at least 1.';
  if (usesMinutes(rule.type)) {
    if (!Number.isFinite(rule.thresholdMinutes) || rule.thresholdMinutes < 0)
      return 'Minutes must be 0 or more.';
    if (rule.type !== 'max_occupancy' && rule.thresholdMinutes === 0)
      return 'Minutes must be more than 0 for this rule.';
  }
  if (
    usesWindow(rule.type) &&
    (rule.windowMinutes <= 0 || rule.windowMinutes > MAX_VISITOR_WINDOW_MINUTES)
  )
    return `Window must be between 1 and ${MAX_VISITOR_WINDOW_MINUTES} minutes.`;
  return null;
}

/**
 * The caution under the minutes field for rules that read silence as
 * evidence: below the camera's own quiet period, an empty zone cannot be
 * told from a still one.
 */
export function livenessHint(
  liveness: PresenceLiveness | undefined,
  rule: PresenceRule
): string | null {
  if (!isLivenessGated(rule.type)) return null;
  const gap = liveness?.p95GapMinutes;
  if (gap == null) {
    return 'This camera only uploads when something moves. Below its quiet period, an empty zone cannot be told from a still one.';
  }
  const rounded = Math.ceil(gap);
  const base = `This camera goes quiet for up to ${rounded} min between clips. Below that, an empty zone can't be told from a still one.`;
  return rule.thresholdMinutes > 0 && rule.thresholdMinutes <= rounded
    ? `${base} Consider more than ${rounded} minutes.`
    : base;
}

export function countHint(
  rule: PresenceRule,
  peakToday: number | null | undefined
): string | null {
  if (rule.type !== 'max_occupancy') return null;
  const seen = peakToday != null ? ` Peak seen today: ${peakToday}.` : '';
  return `Counts are a floor — people hidden behind others are not counted. Set the limit against what this camera has actually seen.${seen}`;
}
